import { Platform } from "react-native";
import { router } from "expo-router";
import sdk from "@farcaster/miniapp-sdk";

/**
 * Patches Expo Router's navigation methods to work in Farcaster Mini Apps.
 *
 * This monkey-patches the global router object, so all existing code
 * using `router.navigate()`, `router.push()`, etc. will automatically work
 * in Farcaster WebViews without any code changes.
 */
export function patchExpoRouterForFarcaster() {
  // Early exit for non-web platforms to prevent any imports or code execution
  if (Platform.OS !== "web") {
    return;
  }

  /**
   * Helper to parse the href from Expo Router's format to
   * a navigable URL string with params interpolated.
   */
  const parseHrefToUrl = (href: any): string | null => {
    if (typeof href === "string") {
      return href;
    }
    if (href && typeof href === "object" && href.pathname && href.params) {
      let url = href.pathname;
      const remainingParams: Record<string, string> = {};

      // Replace bracket params in pathname
      for (const [key, value] of Object.entries(href.params)) {
        const bracketParam = `[${key}]`;
        if (url.includes(bracketParam)) {
          url = url.replace(bracketParam, String(value));
        } else {
          // This param is not in the pathname, add to query params
          remainingParams[key] = String(value);
        }
      }

      // Add remaining params as query string
      if (Object.keys(remainingParams).length > 0) {
        const params = new URLSearchParams(remainingParams);
        url += `?${params.toString()}`;
      }

      return url;
    }
    if (href && typeof href === "object" && href.pathname) {
      return href.pathname;
    }
    return null;
  };

  // const buildUrl = (href: any): string => {
  //   // Reuse parseHrefToUrl to properly handle dynamic route parameters
  //   const parsed = parseHrefToUrl(href);
  //   if (parsed) {
  //     return parsed;
  //   }
  //   // Fallback for edge cases
  //   return typeof href === "string" ? href : String(href);
  // };

  let isInMiniApp: boolean | null = null;

  // Detect if we're in a Farcaster Mini App and store the promise
  const isInMiniAppPromise = sdk
    .isInMiniApp()
    .then((inMiniApp: boolean) => {
      isInMiniApp = inMiniApp;
      return inMiniApp;
    })
    .catch(() => {
      isInMiniApp = false;
      return false;
    });

  /**
   * Helper to get the Mini App detection result, awaiting if needed.
   * This ensures the first navigation call already knows the environment.
   */
  const getIsInMiniApp = async (): Promise<boolean> => {
    if (isInMiniApp !== null) {
      return isInMiniApp;
    }
    return await isInMiniAppPromise;
  };

  // Store original methods
  const originalNavigate = router.navigate?.bind(router);
  const originalPush = router.push?.bind(router);
  const originalReplace = router.replace?.bind(router);
  const originalBack = router.back?.bind(router);

  if (!originalNavigate || !originalPush || !originalReplace || !originalBack) {
    console.warn(
      "[NAV] Could not patch Expo Router - navigation methods not found",
    );
    return;
  }

  router.navigate = async (href: any) => {
    const inMiniApp = await getIsInMiniApp();
    if (inMiniApp) {
      const url = parseHrefToUrl(href);
      if (url) {
        console.log("[NAV] Farcaster: using window.history.pushState to", url);
        window.history.pushState({}, "", url);
        const navEvent = new PopStateEvent("popstate");
        window.dispatchEvent(navEvent);
        return;
      }
    }
    return originalNavigate(href);
  };

  router.push = async (href: any) => {
    const inMiniApp = await getIsInMiniApp();
    if (inMiniApp) {
      const url = parseHrefToUrl(href);
      if (url) {
        console.log(
          "[NAV] Farcaster: using window.history.pushState (push) to",
          url,
        );
        window.history.pushState({}, "", url);
        const navEvent = new PopStateEvent("popstate");
        window.dispatchEvent(navEvent);
        return;
      }
    }
    return originalPush(href);
  };

  router.replace = async (href: any) => {
    const inMiniApp = await getIsInMiniApp();
    if (inMiniApp) {
      const url = parseHrefToUrl(href);
      if (url) {
        console.log(
          "[NAV] Farcaster: using window.history.replaceState to",
          url,
        );
        window.history.replaceState({}, "", url);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }
    }
    return originalReplace(href);
  };

  router.back = async () => {
    const inMiniApp = await getIsInMiniApp();
    if (inMiniApp) {
      console.log("[NAV] Farcaster: window.history.back");
      window.history.back();
      return;
    }
    return originalBack();
  };
}
